import _, { isNil } from 'lodash'

import type ReportStruct from '../ReportStruct'
import Area from './area'
import Bar from './bar'
import DefaultMuncher from './defaultMuncher'
import type Muncher from './index'
import Lines from './lines'
import NumericMetric from './numericMetric'
import Pie from './pie'

interface Props {
  data: Array<Record<string, any>>
  reportStruct: ReportStruct
  chartData: Record<string, any> | undefined | null
  chartType: string
}

export default class MuncherFactory {
  data: Array<Record<string, any>>
  reportStruct: ReportStruct
  chartData: Record<string, any> | undefined | null
  chartType: string

  /**
   * MuncherFactory - This class returns a muncher instance in order to generate
   * the data structure for the chart type selected
   *
   * @constructor
   * @param data - Provide raw data from report content controller
   * @param reportStruct - Provide a report struct in
   * @param chartData - Provide sql report projections
   * @param chartType - Provide a chart type
   */
  constructor({ data, reportStruct, chartData, chartType }: Props) {
    this.data = data
    this.reportStruct = reportStruct
    this.chartData = chartData
    this.chartType = chartType
  }

  /**
   * call - returns a muncher class or a default muncher class
   * if the chart type is a table or a scatterplot
   *
   * @throws Throws an error if there's not an available factory
   *
   */
  call(): Muncher | DefaultMuncher {
    if (['table', 'scatterplot'].includes(this.chartType)) {
      return new DefaultMuncher({ data: this.data })
    }

    return this.build()
  }

  /**
   * buildPayload - returns a sql or a builder payload to use this payload with a muncher class
   *
   * @method
   */
  private buildPayload() {
    const isBuilder = this.reportStruct.isBuilder
    const isSQL = this.reportStruct.isSQL

    if (isBuilder) {
      return this.builderPayload()
    }

    if (isSQL) {
      return this.sqlPayload()
    }

    throw new Error('Report is neither SQL nor builder')
  }

  /**
   * builderPayload - returns a builder payload
   *
   * @method
   */
  private builderPayload() {
    const projection = this.firstProjection()
    const aggregation = this.firstAggregation()

    if (isNil(projection) || isNil(aggregation)) {
      throw new Error("There's no projection and aggregation available to build the payload")
    }

    return {
      data: this.data,
      projection: projection,
      aggregation: aggregation,
    }
  }

  /**
   * builderPayload - returns a sql payload
   *
   * @method
   */
  private sqlPayload() {
    // Given that there are sql numeric reports that were saved without sqlConfiguration,
    // we will assign a empty object to chartData in order not to break numeric reports
    if (
      this.reportStruct.type === 'numeric_metric' &&
      !this.reportStruct.sqlConfiguration &&
      !this.chartData
    ) {
      this.chartData = {}
    }

    if (isNil(this.chartData)) {
      throw new Error("There's no chart data")
    }

    return {
      data: this.data,
      sqlProjections: this.chartData,
    }
  }

  /**
   * build - This method returns a muncher muncher for a specific chart type,
   * build method get the chatType property in order to instance the correct muncher
   */
  private build(): Muncher {
    const payload = this.buildPayload()

    switch (this.chartType) {
      case 'bars': {
        return new Bar(payload)
      }
      case 'pie': {
        return new Pie(payload)
      }
      case 'lines': {
        return new Lines(payload)
      }
      case 'numeric_metric': {
        return new NumericMetric(payload)
      }
      case 'area': {
        return new Area(payload)
      }
    }

    throw new Error(`There isn't a factory to render this chart of type: ${this.chartType}`)
  }

  /**
   * firstProjection - returns the first projection of the report
   *
   * @method
   */
  private firstProjection() {
    return this.reportStruct.projections[0]
  }

  /**
   * firstAggregation - returns the first aggregation projection of the report
   *
   * @method
   */
  private firstAggregation() {
    return this.reportStruct.projections.find((p) => {
      return !_.isEmpty(p.function)
    })
  }
}
